// Results contract + poster.
//
// RunResultsPayload is the stable JSON contract POSTed to the server's ingestion endpoint
// (BP-017 mirrors this shape in C#). The engine only *measures* — the server validates,
// applies tenant scoring/thresholds, persists the run, and returns the share token (PRD §6.1/§13).

import type { FetchLike, ProbeMeasurement, ProbeValue } from './types';

/** One probe's measured result as sent to the server (no client-side scoring). */
export interface ProbeResultDto {
  readonly probeId: string;
  readonly value: ProbeValue;
  readonly blocked: boolean;
  readonly note?: string;
}

/** Stable results payload POSTed to the ingestion endpoint — the shared client/server contract. */
export interface RunResultsPayload {
  readonly engineVersion: string;
  readonly startedAt: string;   // ISO 8601, client clock
  readonly completedAt: string; // ISO 8601, client clock
  /** Vanity slug of the tenant profile; omitted for the anonymous public test (system bucket). */
  readonly profileSlug?: string;
  readonly results: readonly ProbeResultDto[];
}

/** Server response from ingestion: the minted, unguessable share token (PRD §6.3/§13). */
export interface IngestionResponse {
  readonly shareToken: string;
  readonly shareUrl?: string;
}

export interface PostResultsOptions {
  readonly signal?: AbortSignal;
  readonly fetchImpl?: FetchLike;
}

/** Raised when the ingestion endpoint responds with a non-2xx status. */
export class IngestionError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'IngestionError';
  }
}

export function toResultDto(measurement: ProbeMeasurement): ProbeResultDto {
  return {
    probeId: measurement.probeId,
    value: measurement.value,
    blocked: measurement.blocked,
    note: measurement.note,
  };
}

export interface BuildPayloadOptions {
  readonly engineVersion: string;
  readonly startedAt: Date;
  readonly completedAt: Date;
  readonly profileSlug?: string;
}

/** Assembles the stable payload from measurements + run metadata. */
export function buildResultsPayload(
  measurements: readonly ProbeMeasurement[],
  options: BuildPayloadOptions,
): RunResultsPayload {
  return {
    engineVersion: options.engineVersion,
    startedAt: options.startedAt.toISOString(),
    completedAt: options.completedAt.toISOString(),
    profileSlug: options.profileSlug,
    results: measurements.map(toResultDto),
  };
}

/** POSTs the results payload to the ingestion endpoint and returns the minted share token. */
export async function postResults(
  endpoint: string,
  payload: RunResultsPayload,
  options: PostResultsOptions = {},
): Promise<IngestionResponse> {
  const doFetch: FetchLike = options.fetchImpl ?? ((url, init) => fetch(url, init));

  const response = await doFetch(endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
    signal: options.signal,
  });

  if (!response.ok) {
    throw new IngestionError(response.status, `Results ingestion failed with HTTP ${response.status}.`);
  }

  return (await response.json()) as IngestionResponse;
}
